import { useCreateProductInfoRequestMessageMutation } from '@api/productRequest.api';
import { RequestInfoRequestMessageType } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { Button } from '@components';
import { Box, TextField, Typography, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { t } from '@translations';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

const TextArea = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    padding: '0 !important',
    height: 'auto',
  },
}));

const MessageBubble = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: 4,
  marginBottom: theme.spacing(1),
  whiteSpace: 'pre-wrap',
  backgroundColor: theme.palette.background.paper,
}));

type ProductRequestInfoMessagesProps = {
  messages?: RequestInfoRequestMessageType[];
  id?: number;
  question?: string;
  author?: {
    fullName: string;
    email: string;
  };
};

type MessageFormValues = {
  messageText: string;
};

export const ProductRequestInfoMessages: React.FC<ProductRequestInfoMessagesProps> = ({
  messages = [],
  question,
  id,
  author,
}: ProductRequestInfoMessagesProps) => {
  const { register, handleSubmit, reset } = useForm<MessageFormValues>();
  const [createMessage, request] = useCreateProductInfoRequestMessageMutation();
  const { isLoading } = request;
  const [newMessages, setNewMessages] = useState<RequestInfoRequestMessageType[]>([]);
  const visibleMessages = useMemo(() => [...messages, ...newMessages], [messages, newMessages]);

  const onSubmit = (data: MessageFormValues) => {
    if (!id) return;
    const formData = new FormData();
    formData.append('text', data.messageText);
    formData.append('requestId', id.toString());
    createMessage({ formData, requestId: id }).then((res) => {
      if ('data' in res) {
        const newMessage = res.data;
        setNewMessages((prevMessages) => [...prevMessages, newMessage]);
        reset();
      }
    });
  };

  return (
    <Box>
      <Typography color={colors.text.secondary} fontWeight='bold !important'>
        {t(TextCodes.ProductRequest.Info.Messages)}
      </Typography>
      {question ? (
        <Box mb={2} mt={1}>
          <Box mb={1} fontSize='0.9em' color='gray'>
            {author?.fullName}
          </Box>
          <MessageBubble>{question}</MessageBubble>
        </Box>
      ) : null}
      {visibleMessages?.map((message, index) => (
        <Box key={index} mb={2}>
          <Box mb={1} fontSize='0.9em' color='gray'>
            {message.contact.fullName}
          </Box>
          <MessageBubble>{message.text}</MessageBubble>
        </Box>
      ))}
      <Box component='form' onSubmit={handleSubmit(onSubmit)}>
        <TextArea
          label={t(TextCodes.ProductRequest.Info.WriteMessage)}
          fullWidth
          multiline
          required
          rows={4}
          sx={{ mt: 4, mb: 2 }}
          {...register('messageText', { required: true })}
        />
        <Button type='submit' variant='primary' size='small' busy={isLoading}>
          {t(TextCodes.ProductRequest.Info.Send)}
        </Button>
      </Box>
    </Box>
  );
};
